import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination,
  FilterBar, FilterField, Input, RadixSelect, Checkbox,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  toast, type DataTableColumn,
} from '@/components/ui';
import { ArrowLeft, Download } from 'lucide-react';
import { friendlyMessage } from '@/lib/errors';
import { triggerBlobDownload } from '@/features/orders/invoiceBulk/lib/download';
import { downloadEventApplications } from '../hooks/useEventCollectionFieldsQuery';
import {
  useEventApplicationsQuery,
  useEventApplicationDisqualifyMutation,
  useEventApplicationBulkMarkMutation,
  type EventApplication,
  type ApplicationStatus,
} from '../hooks/useEventApplicationsQuery';
import { useEventMutation, useEventQuery } from '../hooks/useEventsQuery';
import { useUserIdentitiesQuery } from '../hooks/useUserIdentitiesQuery';
import type { ContactChannel } from '../types/cms';

const PAGE_SIZE = 20;

const STATUS_OPTIONS: Array<ApplicationStatus | ''> = ['', 'APPLIED', 'WINNER', 'LOSER', 'DISQUALIFIED'];
const ANNOUNCE_CHANNELS: ContactChannel[] = ['EMAIL', 'SMS', 'KAKAO'];

const statusVariant = (status: ApplicationStatus): 'success' | 'danger' | 'warning' | 'info' => {
  if (status === 'WINNER') return 'success';
  if (status === 'DISQUALIFIED') return 'danger';
  if (status === 'LOSER') return 'warning';
  return 'info';
};

export const EventApplicationsPage = () => {
  const { t } = useTranslation('common');
  const { eventId } = useParams<{ eventId: string }>();
  const id = eventId ?? '';

  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<ApplicationStatus | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [disqualifyTarget, setDisqualifyTarget] = useState<EventApplication | null>(null);
  const [disqualifyReason, setDisqualifyReason] = useState('');

  const [announceDialogOpen, setAnnounceDialogOpen] = useState(false);
  const [announceSend, setAnnounceSend] = useState(true);
  const [announceChannel, setAnnounceChannel] = useState<ContactChannel | ''>('');
  const [announceResult, setAnnounceResult] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const eventQuery = useEventQuery(id);
  const query = useEventApplicationsQuery(id, { status, page, size: PAGE_SIZE });
  const disqualify = useEventApplicationDisqualifyMutation(id);
  const bulkMark = useEventApplicationBulkMarkMutation(id);
  const { announce } = useEventMutation();

  const items = query.data?.content ?? [];
  const totalElements = query.data?.totalElements ?? 0;
  const winnersAlreadyAnnounced = eventQuery.data?.isWinnersPublished ?? false;

  // C-7: 현재 페이지 응모자 userId 들을 batch 로 마스킹 신원 조회 (이름/이메일 표시용).
  const pageUserIds = useMemo(() => items.map((r) => r.userId), [items]);
  const identitiesQuery = useUserIdentitiesQuery(pageUserIds);
  const identities = identitiesQuery.data;

  const selectableIds = useMemo(
    () => items.filter((r) => r.status === 'APPLIED').map((r) => r.applicationId),
    [items],
  );
  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id_) => selectedIds.has(id_));
  const hasSelection = selectedIds.size > 0;

  const toggleSelect = (applicationId: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(applicationId);
      else next.delete(applicationId);
      return next;
    });

  const toggleSelectAll = (checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableIds.forEach((id_) => {
        if (checked) next.add(id_);
        else next.delete(id_);
      });
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleReset = () => {
    setStatus('');
    setPage(0);
    clearSelection();
  };

  const handleDisqualify = async () => {
    if (!disqualifyTarget) return;
    const mutateArg: { applicationId: string; reason?: string } = {
      applicationId: disqualifyTarget.applicationId,
    };
    if (disqualifyReason) mutateArg.reason = disqualifyReason;
    try {
      await disqualify.mutateAsync(mutateArg);
      setDisqualifyTarget(null);
      setDisqualifyReason('');
    } catch {
      // error handled via mutation state
    }
  };

  const handleBulkMark = async (target: 'WINNER' | 'LOSER') => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const body = target === 'WINNER'
      ? { winnerIds: ids, loserIds: [] }
      : { winnerIds: [], loserIds: ids };
    try {
      await bulkMark.mutateAsync(body);
      clearSelection();
    } catch {
      // error handled via mutation state
    }
  };

  // QA-416 — 페이지에 보이는 20건이 아니라 이 이벤트의 전체 응모를 BE 가 만든 응모 목록 양식(layout=APPLICATIONS)
  // xlsx 로 받는다. 이벤트 수정 화면의 "응모자 추출"은 layout 없이 기존 양식 그대로.
  const handleExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await downloadEventApplications(id, { layout: 'APPLICATIONS' });
      const title = eventQuery.data?.title?.trim();
      triggerBlobDownload(blob, title ? `응모목록_${title.replace(/[\\/:*?"<>|]/g, '_')}.xlsx` : filename);
      toast.success(t('cms.events.applications.exportDone', { defaultValue: '응모 목록을 다운로드했습니다.' }));
    } catch (err) {
      toast.error(friendlyMessage(err, t('cms.events.applications.exportFail', { defaultValue: '응모 목록을 내려받지 못했습니다.' })));
    } finally {
      setExporting(false);
    }
  };

  const handleAnnounce = async () => {
    setAnnounceResult(null);
    const body = {
      sendNotification: announceSend,
      channel: announceChannel === '' ? null : announceChannel,
    };
    try {
      const res = await announce.mutateAsync({ eventId: id, body });
      setAnnounceResult(
        t('cms.events.applications.announceResult', {
          sentWinners: res.sentWinners,
          sentLosers: res.sentLosers,
          skipped: res.skipped,
        }),
      );
      setAnnounceDialogOpen(false);
    } catch (e) {
      setAnnounceResult(e instanceof Error ? e.message : 'announce 실패');
    }
  };

  const statusOptions = STATUS_OPTIONS.map((s) => ({
    value: s,
    label: s === '' ? t('cms.events.applications.statusFilterAll') : t(`cms.events.applications.status.${s}`),
  }));
  const channelOptions = [
    { value: '', label: t('cms.events.applications.announceChannelDefault') },
    ...ANNOUNCE_CHANNELS.map((c) => ({ value: c, label: t(`cms.events.contactChannel.${c}`) })),
  ];

  const columns: DataTableColumn<EventApplication>[] = [
    {
      key: 'select',
      header: (
        <Checkbox
          aria-label={t('cms.events.applications.selectAll')}
          checked={allSelectableSelected}
          onCheckedChange={(v) => toggleSelectAll(Boolean(v))}
          disabled={selectableIds.length === 0}
        />
      ),
      align: 'center',
      width: 40,
      cell: (row) => (
        <Checkbox
          aria-label={t('cms.events.applications.selectRow')}
          checked={selectedIds.has(row.applicationId)}
          onCheckedChange={(v) => toggleSelect(row.applicationId, Boolean(v))}
          disabled={row.status !== 'APPLIED'}
        />
      ),
    },
    {
      key: 'appliedAt',
      header: t('cms.events.applications.cols.appliedAt'),
      cell: (row) => (
        <span className="text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.appliedAt.slice(0, 10)}
        </span>
      ),
    },
    {
      key: 'userDisplayName',
      header: t('cms.events.applications.cols.user'),
      cell: (row) => {
        // C-7: 마스킹 신원 우선, 없으면 '—' (raw UUID 절대 미노출).
        const identity = identities?.get(row.userId);
        const name = identity?.maskedName ?? '—';
        const email = identity?.maskedEmail;
        return (
          <div className="flex min-w-0 flex-col gap-0.5">
            <span
              className="block max-w-[180px] truncate text-sm"
              style={{ color: 'var(--content-strong)' }}
              title={name}
            >
              {name}
            </span>
            {email ? (
              <span
                className="block max-w-[180px] truncate text-xs"
                style={{ color: 'var(--content-muted)' }}
                title={email}
              >
                {email}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'productName',
      header: t('cms.events.applications.cols.product'),
      cell: (row) => {
        const name = row.productName ?? '—';
        return (
          <span
            className="block max-w-[240px] truncate text-sm"
            style={{ color: 'var(--content-default)' }}
            title={name}
          >
            {name}
          </span>
        );
      },
    },
    {
      key: 'quantity',
      header: t('cms.events.applications.cols.quantity'),
      align: 'right',
      width: 72,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.quantity}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('cms.events.applications.cols.status'),
      align: 'center',
      width: 104,
      cell: (row) => (
        <StatusBadge
          variant={statusVariant(row.status)}
          label={row.status ? t(`cms.events.applications.status.${row.status}`, { defaultValue: '-' }) : '-'}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => (
        row.status === 'APPLIED' ? (
          <RowActions
            triggerLabel={t('cms.events.applications.rowActionsLabel', { defaultValue: '응모 액션' })}
            actions={[
              {
                key: 'disqualify',
                label: t('cms.events.applications.disqualify'),
                variant: 'danger',
                onClick: () => { setDisqualifyTarget(row); setDisqualifyReason(''); },
              },
            ]}
          />
        ) : null
      ),
    },
  ];

  return (
    <PageShell
      title={t('cms.events.applications.pageTitle')}
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleExport()}
            disabled={exporting || !id}
            data-testid="event-applications-excel"
          >
            <Download size={14} className="mr-1" aria-hidden="true" />
            {exporting
              ? t('cms.events.applications.exporting', { defaultValue: '만드는 중…' })
              : t('cms.events.applications.excel', { defaultValue: '엑셀 다운로드' })}
          </Button>
          <Link to={`/cms/events/${id}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft size={14} className="mr-1" aria-hidden="true" />
              {t('cms.events.applications.backToEvent')}
            </Button>
          </Link>
          <Link to={`/cms/events/${id}/winners`}>
            <Button variant="secondary" size="sm">
              당첨자 발표
            </Button>
          </Link>
        </div>
      }
      data-testid="event-applications-page"
    >
      <FilterBar
        filters={
          <FilterField label={t('cms.events.applications.cols.status')}>
            <RadixSelect
              value={status}
              onChange={(v) => { setStatus(v as ApplicationStatus | ''); setPage(0); clearSelection(); }}
              options={statusOptions}
            />
          </FilterField>
        }
        actions={
          <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
            {t('cms.reset', { defaultValue: '초기화' })}
          </Button>
        }
      />

      {/* 132: 응모 상태 4종(응모중/당첨/낙첨/무효)의 운영 정책 의미를 인라인 범례로 안내한다.
       * APPLIED 만 자동(결제 시) 생성, WINNER/LOSER/DISQUALIFIED 는 운영자 수동 처리(시스템 랜덤 추첨 없음).
       * 라벨은 i18n status 표를 사용 — raw enum 명은 노출하지 않는다. */}
      <details className="rounded-md border px-3 py-2 text-xs" style={{ borderColor: 'var(--border-subtle)' }}>
        <summary className="cursor-pointer font-medium" style={{ color: 'var(--content-muted)' }}>
          {t('cms.events.applications.statusLegend.title', { defaultValue: '상태 안내' })}
        </summary>
        <ul className="mt-2 flex flex-col gap-1.5">
          {(['APPLIED', 'WINNER', 'LOSER', 'DISQUALIFIED'] as const).map((s) => (
            <li key={s} className="flex items-start gap-2">
              <StatusBadge variant={statusVariant(s)} label={t(`cms.events.applications.status.${s}`)} />
              <span style={{ color: 'var(--content-muted)' }}>
                {t(`cms.events.applications.statusLegend.${s}`)}
              </span>
            </li>
          ))}
        </ul>
      </details>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!hasSelection || bulkMark.isPending}
          onClick={() => void handleBulkMark('WINNER')}
        >
          {t('cms.events.applications.markWinner', { count: selectedIds.size })}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!hasSelection || bulkMark.isPending}
          onClick={() => void handleBulkMark('LOSER')}
        >
          {t('cms.events.applications.markLoser', { count: selectedIds.size })}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => { setAnnounceDialogOpen(true); setAnnounceResult(null); }}
        >
          {winnersAlreadyAnnounced
            ? t('cms.events.applications.announceAgain')
            : t('cms.events.applications.announce')}
        </Button>
        {announceResult && (
          <span className="text-xs" style={{ color: 'var(--content-muted)' }}>{announceResult}</span>
        )}
      </div>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.events.applications.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.applicationId}
          emptyState={t('cms.events.applications.empty')}
          stickyHeader
        />
      )}

      <Pagination
        total={totalElements}
        currentPage={page + 1}
        pageSize={PAGE_SIZE}
        onPageChange={(p) => setPage(p - 1)}
        className="mt-4"
      />

      <Dialog
        open={disqualifyTarget !== null}
        onOpenChange={(open) => { if (!open) { setDisqualifyTarget(null); setDisqualifyReason(''); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.events.applications.disqualifyTitle')}</DialogTitle>
            <DialogDescription>{t('cms.events.applications.disqualifyDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
              {t('cms.events.applications.disqualifyReason')}
            </label>
            <Input
              type="text"
              value={disqualifyReason}
              onChange={(e) => setDisqualifyReason(e.target.value)}
              placeholder={t('cms.events.applications.disqualifyReasonPlaceholder')}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={disqualify.isPending}
              onClick={() => void handleDisqualify()}
            >
              {t('cms.events.applications.disqualifyConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={announceDialogOpen}
        onOpenChange={(open) => { if (!open) setAnnounceDialogOpen(false); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.events.applications.announceTitle')}</DialogTitle>
            <DialogDescription>{t('cms.events.applications.announceDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={announceSend}
                onCheckedChange={(v) => setAnnounceSend(Boolean(v))}
              />
              <span style={{ color: 'var(--content-default)' }}>
                {t('cms.events.applications.announceSendNotification')}
              </span>
            </label>
            <label className="flex flex-col gap-1" style={{ opacity: announceSend ? 1 : 0.5 }}>
              <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('cms.events.applications.announceChannel')}
              </span>
              <div className="max-w-[240px]">
                <RadixSelect
                  value={announceChannel}
                  onChange={(v) => setAnnounceChannel((v || '') as ContactChannel | '')}
                  disabled={!announceSend}
                  options={channelOptions}
                />
              </div>
            </label>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              size="sm"
              disabled={announce.isPending}
              onClick={() => void handleAnnounce()}
            >
              {t('cms.events.applications.announceConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
